/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { AppConfigService } from '@app/common/app-config.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';

const log = new Logger('AiFindingService');

@Injectable({
  providedIn: 'root'
})
export class AiFindingService {
  constructor(
    private appConfigService: AppConfigService,
    private userPreferenceService: UserPreferenceService,
    private dataService: ProxyDataService,
    private notify: NotifyService,
    private translate: TranslateService
  ) {}

  openAIRerunDialog() {
    return this.notify.decision(this.translate.instant('ai.algorithm.rerun.confirmationText'), {
      headerContent: this.translate.instant('ai.algorithm.rerun.confirmationHeader'),
      noButtonLabel: this.translate.instant('label.cancel'),
      yesButtonLabel: this.translate.instant('ai.algorithm.rerun.confirmationYes'),
      isDecisionModal: true,
      enableYesBtn: true
    });
  }

  isShowAiFindingsEnabled() {
    return (
      this.userPreferenceService.getUserPreferenceFromCache('show-ai-findings') === true &&
      this.appConfigService.getAppConfigByKey('ai.findings.enabled') === 'ON'
    );
  }

  isShowAiFindingsOnlyAdminEnabled() {
    return this.appConfigService.getAppConfigByKey('ai.findings.enabled') === 'ON';
  }

  async rerunAIFindings(exam) {
    const data: any = await this.getExamAIFindingsDetails(exam.id || exam.requestedProcedureId);
    if (data.aiStatus !== -1) {
      await this.openAIRerunRefreshWorklistDialog();
    } else {
      await this.callAIRerunService(exam);
    }
  }

  private async callAIRerunService(exam) {
    const procId = exam.requestedProcedureId || exam.id;
    const requestData = { examCKey: procId, examAuditInfo: exam.examAuditInfo };

    try {
      await this.dataService.update('requested-procedures', 'ai-findings', { aiFindingExamDtls: [requestData] });
      this.notify.info(this.translate.instant('ai.algorithm.rerun.success'));
    } catch (error) {
      log.error('Error occurred while calling CPACS Service to Rerun AI Findings ' + error);
      this.notify.warning(this.translate.instant('ai.algorithm.rerun.failed'));
      throw error;
    }
  }

  private openAIRerunRefreshWorklistDialog() {
    return this.notify.decision(this.translate.instant('ai.algorithm.rerun.refreshWorklistText'), {
      headerContent: this.translate.instant('ai.algorithm.rerun.refreshWorklistHeader'),
      isDecisionModal: false,
      enableYesBtn: true
    });
  }

  async getExamAIFindingsDetails(requestedProcedureId) {
    try {
      const data = await this.dataService.getById('requested-procedures', requestedProcedureId + '/ai-findings');
      log.debug('getExamAIFindingsDetails for ID: ' + requestedProcedureId);
      return data;
    } catch (error) {
      log.error(
        'getExamAIFindingsDetails:: Error occurred while receiving exam ai finding details. Error=>' +
          JSON.stringify(error)
      );
      throw error;
    }
  }
}
